import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Spinner,
    Button
} from 'reactstrap';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import Header from '../../components/Headers/Header';
import { FaDownload, FaPrint } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './ViewExam.css';
import Footer from 'components/Footers/Footer';
import {
    createLandscapePdf,
    standardAutoTableOptions,
    drawCenteredFooter,
} from '../../utils/pdfLayout';

const baseURL = apiURL1;

function ViewStudentResult() {
    const token = localStorage.getItem("token");

    const { studentId, type, year } = useParams();
    const location = useLocation();
    const { className, typeName } = location.state || {};

    const [studentData, setStudentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [schoolInfo, setSchoolInfo] = useState({});

    useEffect(() => {
        fetchResults();
        fetchSchoolInfo();
    }, [studentId, type, year]);

    const fetchSchoolInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            const data = response.data;

            if (data && data.length > 0) {
                setSchoolInfo(data[0]);
            }
        } catch (error) {
            console.error('Error fetching school info:', error);
        }
    };

    const fetchResults = async () => {
        setLoading(true);
        try {
            let response;
            try {
                response = await axios.get(
                    `${baseURL}/results/student/${studentId}/type/${type}/year/${year}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
            } catch (primaryErr) {
                response = await axios.get(
                    `${baseURL}/superadmin/results/student/${studentId}/type/${type}/year/${year}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
            }
            // console.log('Student data is ', response.data.data)
            setStudentData(response.data.data);
        } catch (error) {
            // console.error('Error fetching results:', error);
            toast.error('Error fetching results');
        } finally {
            setLoading(false);
        }
    };

    const generatePdfReport = () => {
        if (!studentData) return;

        const pdf = createLandscapePdf(jsPDF);
        const pageWidth = pdf.internal.pageSize.getWidth();

        const img = new Image();
        img.src = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";
        try {
            pdf.addImage(img, 'PNG', 12, 8, 22, 22);
        } catch (e) { /* optional */ }

        pdf.setFontSize(13);
        pdf.setFont('helvetica', 'bold');
        pdf.text(schoolInfo.schoolName || 'Ummu Dhardha Ladies Islamic College', 40, 14);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(schoolInfo.schoolAddress || 'Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.', 40, 20);
        pdf.text(`Tel: ${schoolInfo.schoolPhoneNumber || '+94 67 205 3211'} | Email: ${schoolInfo.schoolEmail || 'info@ummudhardha.lk'}`, 40, 25);

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Student Result — ${studentData.examType || ''} ${year || ''}`.trim(), pageWidth / 2, 34, { align: 'center' });

        let y = 42;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        const infoLeft = [
            `Name: ${studentData.fullName || '-'}`,
            `Registration No: ${studentData.registrationNumber || '-'}`,
            `Class: ${className || '-'}`,
            `Exam Type: ${studentData.examType || '-'}`,
        ];
        const infoRight = [
            `Year: ${studentData.year || year || '-'}`,
            `Total / Obtained: ${studentData.totalMarks ?? '-'} / ${studentData.obtainedMarks ?? '-'}`,
            `Percentage: ${studentData.overallPercentage ?? '-'}%`,
            `Grade: ${studentData.overallGrade || '-'}  |  Rank: ${studentData.rank ?? '-'}`,
        ];
        infoLeft.forEach((line, i) => pdf.text(line, 12, y + i * 5));
        infoRight.forEach((line, i) => pdf.text(line, pageWidth / 2 + 10, y + i * 5));
        y += 28;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Subject Results', 12, y);
        y += 4;

        const headers = [['Code', 'Subject', 'Mid', 'Final', 'Total', 'Obtained', 'Grade']];

        const tableData = (studentData.subjects || []).map((subject) => {
            const midPair = `${subject.midSubjectObtainedMarks ?? '-'}/${subject.midSubjectTotalMarks ?? '-'}`;
            const finalPair = `${subject.subjectObtainMarksFinal ?? '-'}/${subject.subjectTotalMarksFinal ?? '-'}`;
            return [
                subject.subjectCode || '-',
                subject.subjectName || '-',
                midPair,
                finalPair,
                subject.grandTotal ?? subject.totalMarks ?? '-',
                subject.grandObtained ?? '-',
                subject.grade || '-',
            ];
        });

        pdf.autoTable(standardAutoTableOptions({
            head: headers,
            body: tableData,
            startY: y,
            columnStyles: {
                0: { cellWidth: 22 },
                1: { cellWidth: 70, halign: 'left' },
                2: { cellWidth: 28 },
                3: { cellWidth: 28 },
                4: { cellWidth: 24 },
                5: { cellWidth: 26 },
                6: { cellWidth: 20 },
            },
        }));

        drawCenteredFooter(pdf, schoolInfo.schoolName);
        pdf.save(`student-result-${studentData.fullName}-${studentData.examType}-${year}.pdf`);
    };

    const handlePrint = () => {
        if (!studentData) return;

        const tableStyle = `border-collapse: collapse; width: 100%; margin: 10px 0;`; // Added margin
        const thStyle = `background-color: #2980b9; color: white; padding: 8px; text-align: left; border: 1px solid #ddd;`;
        const tdStyle = `padding: 8px; text-align: left; border: 1px solid #ddd;`;

        const globalComponentNamesPrint = (() => {
            const set = new Set();
            (studentData.subjects || []).forEach(subject => {
                (subject.components || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) set.add(n); });
                (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) set.add(n); });
            });
            return Array.from(set);
        })();

        const printContent = `
            <html>
            <head>
                <title>Student Result - ${studentData.fullName}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; } // Added margin to body
                    .header { text-align: center; margin-bottom: 20px; }
                    .school-name { font-size: 24px; font-weight: bold; }
                    .school-info { font-size: 14px; }
                    .result-title { font-size: 20px; margin-bottom: 10px; }
                    table { ${tableStyle} }
                    th { ${thStyle} }
                    td { ${tdStyle} }
                    .footer { text-align: center; margin-top: 20px; font-size: 12px; }
                    div { margin-bottom: 5px; } // Added space between text
                </style>
            </head>
            <body>
                <div class="header">
                    <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp" alt="School Logo" width="100"/>
                    <div class="school-name">${schoolInfo.schoolName || 'Ummu Dhardha Ladies Islamic College'}</div>
                    <div class="school-info">
                        ${schoolInfo.schoolAddress || 'Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.'}<br/>
                        Tel: ${schoolInfo.schoolPhoneNumber || '+94 67 205 3211'} | Email: ${schoolInfo.schoolEmail || 'info@ummudhardha.lk'}
                    </div>
                </div>
                <div class="result-title">Student Result - ${studentData.fullName} (${studentData.examType} ${year})</div>
                <div><strong>Class:</strong> ${className}</div>
                <div><strong>Name:</strong> ${studentData.fullName}</div>
                <div><strong>Registration Number:</strong> ${studentData.registrationNumber}</div>
                <div><strong>Exam Type:</strong> ${studentData.examType}</div>
                <div><strong>Year:</strong> ${studentData.year}</div>
                <div><strong>Total Marks:</strong> ${studentData.totalMarks}</div>
                <div><strong>Obtained Marks:</strong> ${studentData.obtainedMarks}</div>
                <div><strong>Rank:</strong> ${studentData.rank}</div>
                <div><strong>Percentage:</strong> ${studentData.overallPercentage}</div>
                <div><strong>Grade:</strong> ${studentData.overallGrade}</div>
                
                <h3>Subject Results</h3>
                <table border="1">
                    <thead>
                        <tr>
                            <th>Subject Code</th>
                            <th>Subject Name</th>
                            <th>Mid Subject</th>
                            ${globalComponentNamesPrint.map(n => `<th>${n}</th>`).join('')}
                            <th>Final Subject</th>
                            <th>Grand Total</th>
                            <th>Grand Obtained</th>
                            <th>Grade</th>
                            <th>Comments</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${studentData.subjects.map(subject => {
                            const totals = new Map();
                            (subject.components || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) totals.set(n, c?.totalMarks ?? '-'); });
                            const obtained = new Map();
                            (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) obtained.set(n, c?.marks ?? '-'); });

                            return `
                                <tr>
                                    <td>${subject.subjectCode}</td>
                                    <td>${subject.subjectName}</td>
                                    <td>${(subject.midSubjectObtainedMarks ?? '-')}/${(subject.midSubjectTotalMarks ?? '-')}</td>
                                    ${globalComponentNamesPrint.map(n => `<td>${(obtained.get(n) ?? '-')}/${(totals.get(n) ?? '-')}</td>`).join('')}
                                    <td>${(subject.subjectObtainMarksFinal ?? '-')}/${(subject.subjectTotalMarksFinal ?? '-')}</td>
                                    <td>${subject.grandTotal ?? subject.totalMarks}</td>
                                    <td>${subject.grandObtained ?? '-'}</td>
                                    <td>${subject.grade}</td>
                                    <td>${subject.remarks}</td>
                                </tr>
                            `;
                        }).join('')}
                    </tbody>
                </table>
                <div class="footer">Generated on: ${new Date().toLocaleDateString()} | ${schoolInfo.schoolName || 'Ummu Dhardha Ladies Islamic College'}</div>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
    };

    if (loading) {
        return (
            <>
                <Header />
                <Container>
                    <Row>
                        <Col>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Spinner color="primary" />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    if (!studentData) {
        return (
            <>
                <Header />
                <Container>
                    <Row>
                        <Col>
                            <p>No result found for this student.</p>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    return (
        <>
            <Header />
            <Container>
                <Row>
                    <Col>
                        <Card>
                            <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3>Student Result</h3>
                                <div>
                                    <Button color="success" size="sm" onClick={generatePdfReport} style={{ marginRight: '10px' }}>
                                        <FaDownload /> Download PDF
                                    </Button>
                                    <Button color="info" size="sm" onClick={handlePrint}>
                                        <FaPrint /> Print
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <h4>Class: {className}</h4>
                                <h4>Name: {studentData.fullName}</h4>
                                <h4>Registration Number: {studentData.registrationNumber}</h4>
                                <h4>Exam Type: {studentData.examType}</h4>
                                <h4>Year: {studentData.year}</h4>
                                <h4>Total Marks: {studentData.totalMarks}</h4>
                                <h4>Obtained Marks: {studentData.obtainedMarks}</h4>
                                <h4>Rank: {studentData.rank}</h4>
                                <h4>Percentage: {studentData.overallPercentage}</h4>
                                <h4>Grade: {studentData.overallGrade}</h4>

                                <h5>Subject Results</h5>
                                <div className="table-responsive">
                                    <Table className="table">
                                        <thead>
                                            <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                                <th>Subject Code</th>
                                                <th>Subject Name</th>

                                                <th>Mid Subject</th>
                                                {(() => {
                                                    const set = new Set();
                                                    (studentData.subjects || []).forEach(subject => {
                                                        (subject.components || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) set.add(n); });
                                                        (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) set.add(n); });
                                                    });
                                                    const names = Array.from(set);
                                                    return names.map(n => (<th key={`comp-h-${n}`}>{n}</th>));
                                                })()}
                                                <th>Final Subject</th>
                                                <th>Grand Total</th>
                                                <th>Grand Obtained</th>
                                                <th>Grade</th>
                                                <th>Percentage</th>
                                                <th>Comments</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {studentData.subjects.map(subject => (
                                                <tr key={subject.subjectCode}>
                                                    <td>{subject.subjectCode}</td>
                                                    <td>{subject.subjectName}</td>
                                                    <td>{`${(subject.midSubjectObtainedMarks ?? '-')}/${(subject.midSubjectTotalMarks ?? '-')}`}</td>
                                                    {(() => {
                                                        const totals = new Map();
                                                        (subject.components || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) totals.set(n, c?.totalMarks ?? '-'); });
                                                        const obtained = new Map();
                                                        (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) obtained.set(n, c?.marks ?? '-'); });
                                                        const set = new Set();
                                                        (studentData.subjects || []).forEach(s => {
                                                            (s.components || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) set.add(n); });
                                                            (s.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) set.add(n); });
                                                        });
                                                        const names = Array.from(set);
                                                        return names.map(n => (
                                                            <td key={`comp-${subject.subjectCode}-${n}`}>{`${(obtained.get(n) ?? '-')}/${(totals.get(n) ?? '-')}`}</td>
                                                        ));
                                                    })()}
                                                    <td>{`${(subject.subjectObtainMarksFinal ?? '-')}/${(subject.subjectTotalMarksFinal ?? '-')}`}</td>
                                                    <td>{subject.grandTotal ?? subject.totalMarks}</td>
                                                    <td>{subject.grandObtained ?? '-'}</td>

                                                    <td>{subject.grade}</td>
                                                    <td>{subject.percentage}</td>
                                                    <td>{subject.remarks}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Footer />
            </Container>
        </>
    );
}

export default ViewStudentResult;
